import './SADeleteUsersPermanentlyConfirmationModal.scss'
import {Button} from "../../../../Form/Button/Button";
import {buttonColors} from "../../../../Pickers/InputTheme";
import React, {useContext, useEffect, useState, useMemo} from "react";
import {Modal} from "../../../../Smart/Modal/Modal";
import {SA_bulkPermanentlyDeleteUsers} from "../../../../../Services/SA_api";
import {GridReactContext} from "../../../../../Utils/DataGridWrapper/GridContext";
import {I18nReactContext} from "../../../../../contexts/I18nContext";
import {DataTable} from "../../../../DataTable/DataTable";
import {ScrollableListCell} from "../../../../../Utils/ScrollableListCell/ScrollableListCell";
import {get} from "lodash";
import {TextField} from "@mui/material";

/**
 * Modal component for Super Admin to confirm permanent bulk user deletion.
 * Requires typing confirmation text to authorize the deletion.
 * CRITICAL: This action cannot be undone - permanently removes users from database.
 *
 * @param {object} params - The component parameters
 * @param {boolean} params.deleteUsersPermanentlyModalOpen - Whether the modal is open
 * @param {function} params.onCloseDeleteUsersPermanentlyModal - Function to close the modal
 * @param {function} params.onSuccess - Function to call on successful deletion
 * @returns {JSX.Element} The SA delete users permanently confirmation modal
 */
export const SADeleteUsersPermanentlyConfirmationModal = ({
    deleteUsersPermanentlyModalOpen,
    onCloseDeleteUsersPermanentlyModal,
    onSuccess
}) => {
    const [deleting, setDeleting] = useState(false)
    const [error, setError] = useState(null)
    const [confirmationText, setConfirmationText] = useState('')
    const gridContext = useContext(GridReactContext)
    const {i18n} = useContext(I18nReactContext)

    // Memoize selectionModel to prevent unnecessary re-renders
    const selectionModel = useMemo(() => gridContext.selectionModel, [gridContext.selectionModel])

    // Use selectedRows which contains the FULL data for selected users
    const usersToDeleteWithData = useMemo(() => {
        return gridContext.selectedRows || [];
    }, [gridContext.selectedRows])

    // Get the required confirmation text from i18n
    const requiredConfirmationText = i18n.translate('SADeleteUsersPermanentlyModal.confirmationText')

    // Check if confirmation text matches (case-insensitive, trimmed)
    const isConfirmationValid = confirmationText.trim().toLowerCase() === requiredConfirmationText.toLowerCase()

    // Reset state when modal closes
    useEffect(() => {
        if (!deleteUsersPermanentlyModalOpen) {
            setError(null);
            setConfirmationText('');
        }
    }, [deleteUsersPermanentlyModalOpen])

    const onDeleteUsersPermanently = async () => {
        if (!isConfirmationValid) {
            setError(i18n.translate('SADeleteUsersPermanentlyModal.confirmationMismatch'))
            return
        }

        try {
            setDeleting(true)
            setError(null)
            await SA_bulkPermanentlyDeleteUsers({userIds: selectionModel})
            gridContext.triggerTableRefresh()
            onSuccess && onSuccess()
        }
        catch (e) {
            console.error('Error permanently deleting users:', e)
            setError(i18n.translate('SADeleteUsersPermanentlyModal.deleteError'))
            setDeleting(false)
        }
    }

    const columns = [
        {
            key: 'firstName',
            label: i18n.translate('SADeleteUsersPermanentlyModal.firstName'),
            width: 150
        },
        {
            key: 'lastName',
            label: i18n.translate('SADeleteUsersPermanentlyModal.lastName'),
            width: 150
        },
        {
            key: 'email',
            label: i18n.translate('SADeleteUsersPermanentlyModal.email'),
            width: 300
        },
        {
            key: 'phone',
            label: i18n.translate('SADeleteUsersPermanentlyModal.phone'),
            width: 170
        },
        {
            key: 'employeeId',
            label: i18n.translate('SADeleteUsersPermanentlyModal.employeeID'),
            width: 150
        },
        {
            key: 'idNumber',
            label: i18n.translate('SADeleteUsersPermanentlyModal.idNumber'),
            width: 150
        },
        {
            key: 'organizations',
            label: i18n.translate('SADeleteUsersPermanentlyModal.organizations'),
            width: 300,
            render: (user) => {
                const organizations = get(user, 'row.organizations', []);
                if (organizations.length === 0) {
                    return '-';
                }
                const organizationNames = organizations.map((org) => get(org, 'orgName', '')).join(', ');
                return <ScrollableListCell value={organizationNames} />;
            }
        }
    ]

    return <Modal
        open={deleteUsersPermanentlyModalOpen}
        onClose={onCloseDeleteUsersPermanentlyModal}
        data-testid="sa-delete-permanently-confirmation-modal">
        <div className="SADeleteUsersPermanentlyModal modalWrapper">
            <div className="modalTitle">
                <div>{i18n.translate('SADeleteUsersPermanentlyModal.title')}</div>
            </div>
            <div className="modalContent">
                {/* Warning Section */}
                <div className="SADeleteUsersPermanentlyModal__warning-section">
                    <div className="SADeleteUsersPermanentlyModal__warning-title">
                        ⚠️ {i18n.translate('SADeleteUsersPermanentlyModal.warningTitle')}
                    </div>
                    <div className="SADeleteUsersPermanentlyModal__warning-message">
                        {i18n.translate('SADeleteUsersPermanentlyModal.warningMessage', {
                            context: {
                                userCount: usersToDeleteWithData.length
                            }
                        })}
                    </div>
                </div>

                {/* Error State */}
                {error && (
                    <div data-testid="error-message" className="SADeleteUsersPermanentlyModal__error-message">
                        {error}
                    </div>
                )}

                {/* Users Preview Table */}
                <div className="SADeleteUsersPermanentlyModal__users-preview">
                    <DataTable
                        columns={columns}
                        data={usersToDeleteWithData}
                        getRowKey={(row) => row.id}
                        className="usersToDeletePermanently"
                    />
                </div>

                {/* Confirmation Input Section */}
                <div className="SADeleteUsersPermanentlyModal__confirmation-section">
                    <div className="SADeleteUsersPermanentlyModal__confirmation-label">
                        {i18n.translate('SADeleteUsersPermanentlyModal.confirmationRequired')}
                    </div>
                    <div className="SADeleteUsersPermanentlyModal__confirmation-text-display">
                        <strong>{requiredConfirmationText}</strong>
                    </div>
                    <TextField
                        fullWidth
                        value={confirmationText}
                        onChange={(e) => setConfirmationText(e.target.value)}
                        placeholder={i18n.translate('SADeleteUsersPermanentlyModal.confirmationPlaceholder')}
                        disabled={deleting}
                        error={!isConfirmationValid && confirmationText.length > 0}
                        helperText={!isConfirmationValid && confirmationText.length > 0 ?
                            i18n.translate('SADeleteUsersPermanentlyModal.confirmationMismatch') : ''}
                        data-testid="confirmation-input"
                        className="SADeleteUsersPermanentlyModal__confirmation-input"
                    />
                </div>
            </div>
            <div className="modalFooter">
                <Button
                    data-testid="cancel-button"
                    color={buttonColors.YELLOW}
                    onClick={onCloseDeleteUsersPermanentlyModal}
                    disabled={deleting}
                    text={i18n.translate('SADeleteUsersPermanentlyModal.cancel')}
                />
                <Button
                    data-testid="delete-permanently-button"
                    disabled={deleting || !isConfirmationValid || selectionModel.length === 0}
                    color={buttonColors.rejection}
                    onClick={onDeleteUsersPermanently}
                    text={deleting
                        ? i18n.translate('SADeleteUsersPermanentlyModal.deleting')
                        : i18n.translate('SADeleteUsersPermanentlyModal.deleteUsersPermanently')}
                />
            </div>
        </div>
    </Modal>
}
